import PreviewPage from '@/pages/FileLibPage/components/preview'
import { Image } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import type { PluggableList } from 'unified'

const markdownRemarkPlugins: PluggableList = [remarkGfm, remarkMath]
const markdownRehypePlugins: PluggableList = [rehypeRaw as unknown as any]

interface PopoverContProps {
  info: any // 溯源信息
  title?: string // 标题
  onClose?: () => void // 添加关闭回调函数
}

const PopoverCont: React.FC<PopoverContProps> = ({ info, title = '知识点溯源', onClose }) => {
  const imgListRef = useRef(null)

  // 滑动状态管理
  const [canScrollLeft, setCanScrollLeft] = useState(false) // 是否可以向左滑动
  const [canScrollRight, setCanScrollRight] = useState(true) // 是否可以向右滑动

  // 监听滚动事件，更新滑动状态
  const handleScroll = () => {
    if (!imgListRef.current) return
    const { scrollLeft, clientWidth, scrollWidth } = imgListRef.current

    // 判断是否可以向左滑动
    setCanScrollLeft(scrollLeft > 5)

    // 判断是否可以向右滑动
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5)
  }

  // 向右滑动
  const handleScrollRight = () => {
    if (imgListRef.current) {
      imgListRef.current.scrollBy({
        left: 80,
        behavior: 'smooth'
      })
      // 滚动后延迟检查状态
      setTimeout(() => {
        handleScroll()
      }, 300)
    }
  }

  // 向左滑动
  const handleScrollLeft = () => {
    if (imgListRef.current) {
      imgListRef.current.scrollBy({
        left: -80,
        behavior: 'smooth'
      })
    }
  }

  console.log('info', info)
  /* 文件展示 溯源img高亮 */
  const [previewOpen, setPreviewOpen] = useState(false)

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  const image_url_dict = info?.detail?.[0]?.image_url_dict ?? []

  // 图片预加载函数，解决马赛克问题
  const preloadImage = (src: string) => {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img')
      img.onload = () => resolve(src)
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`))
      img.src = src
    })
  }

  // 预加载所有图片
  useEffect(() => {
    if (image_url_dict && Object.values(image_url_dict).length > 0) {
      Object.values(image_url_dict).forEach((src: string) => {
        preloadImage(src).catch(error => {
          console.warn('图片预加载失败:', error)
        })
      })
    }
  }, [image_url_dict])

  // 智能内容渲染函数，优先按 Markdown 渲染，失败则回退纯文本
  const renderMixedContent = (content: string) => {
    if (!content) return null

    try {
      return (
        <ReactMarkdown
          // className="inline-block max-w-full whitespace-break-spaces break-all text-sm"
          className="max-w-full"
          remarkPlugins={markdownRemarkPlugins}
          rehypePlugins={markdownRehypePlugins}
          linkTarget="_blank"
          components={{
            img: ({ node, ...props }) => <img style={{ maxWidth: '100%' }} {...props} />,
            a: ({ node, ...props }) => (
              // <a {...props} target="_blank" rel="noopener noreferrer">
              //   {props.children}
              // </a>
              <span {...props}>
                {props.children}
              </span>
            )
          }}
        >
          {content}
        </ReactMarkdown>
      )
    } catch (error) {
      return <div className="inline-block max-w-full whitespace-break-spaces break-all text-sm">{content}</div>
    }
  }

  function isMarkdownFile(fileName) {
    if (!fileName) return false

    const markdownExtensions = ['.md']
    const lowerFileName = fileName.toLowerCase()

    return markdownExtensions.some(ext => lowerFileName.endsWith(ext))
  }

  return (
    <>
      <div className="Popover_contCase caseOverFlowY flexCol">
        {/* head */}
        <div className="flexRowStart flexRowJustBet">
          <span className="headName">{title}</span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleClose}
            style={{ cursor: 'pointer' }} // 添加鼠标指针样式
            className="hover:opacity-70" // 添加hover效果（可选）
          >
            <path d="M4 4L12 12" stroke="#232E3E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 12L12 4" stroke="#232E3E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {/* pdf */}
        <span className="pdfTitle cursonCase mt-[12px]" onClick={() => setPreviewOpen(true)}>
          {info?.source}
        </span>
        {/* 切片信息 */}
        <div className="sliceCase flexCol mt-[8px]">
          {/* 切片 */}
          <span className="slicehead">命中分片：{info?.detail?.[0]?.chunk_id}</span>
          {/* content */}
          <div className="caseOverFlowY sliceContCase">{renderMixedContent(info?.content)}</div>
          {/* img */}
          {Object.keys(image_url_dict).length > 0 && (
            <div className="flexRowStart relative">
              <div className="img_list flexRowStart caseOverFlowX cursonCase" ref={imgListRef} onScroll={handleScroll}>
                <Image.PreviewGroup items={Object.values(image_url_dict)}>
                  {Object.values(image_url_dict).map((item: string) => {
                    return (
                      <div className="popImageCase" key={item}>
                        <Image
                          width={80}
                          height={75}
                          className="img_case m-0 p-0"
                          src={item}
                          placeholder={
                            <div
                              className="img-placeholder"
                              style={{
                                width: 80,
                                height: 75,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#999',
                                fontSize: '12px'
                              }}
                            >
                              加载中...
                            </div>
                          }
                          onError={e => {
                            console.error('图片加载失败:', item, e)
                          }}
                          onLoad={e => {
                            // 图片加载成功后的处理
                            const img = e.target as HTMLImageElement
                            if (img) {
                              img.style.opacity = '1'
                            }
                          }}
                          preview={{
                            mask: '点击预览',
                            maskClassName: 'custom-mask'
                          }}
                          style={{
                            transition: 'opacity 0.3s ease',
                            opacity: 0
                          }}
                        />
                      </div>
                    )
                  })}
                </Image.PreviewGroup>
              </div>
              {/* 左侧滑动按钮 */}
              {Object.keys(image_url_dict).length > 4 && canScrollLeft && (
                <svg
                  className="img_leftIcon cursonCase"
                  onClick={e => {
                    e.stopPropagation()
                    handleScrollLeft()
                  }}
                  width="34"
                  height="44"
                  viewBox="0 0 34 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ left: '0px', position: 'absolute', zIndex: 10 }}
                >
                  <g filter="url(#filter0_ddd_888_6174)">
                    <circle cx="12" cy="22" r="12" fill="white" />
                    <path
                      d="M14 18L10 22L14 26"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_ddd_888_6174"
                      x="0"
                      y="0"
                      width="44"
                      height="44"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect1_dropShadow_888_6174" />
                      <feOffset dy="2" />
                      <feGaussianBlur stdDeviation="2" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0" />
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_888_6174" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="4" />
                      <feGaussianBlur stdDeviation="2.5" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0" />
                      <feBlend mode="normal" in2="effect1_dropShadow_888_6174" result="effect2_dropShadow_888_6174" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset />
                      <feGaussianBlur stdDeviation="5" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0" />
                      <feBlend mode="normal" in2="effect2_dropShadow_888_6174" result="effect3_dropShadow_888_6174" />
                      <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow_888_6174" result="shape" />
                    </filter>
                  </defs>
                </svg>
              )}

              {/* 右侧滑动按钮 */}
              {Object.keys(image_url_dict).length > 4 && canScrollRight && (
                <svg
                  className="img_rightIcon cursonCase"
                  onClick={e => {
                    e.stopPropagation()
                    handleScrollRight()
                  }}
                  width="34"
                  height="44"
                  viewBox="0 0 34 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_ddd_888_6173)">
                    <circle cx="22" cy="22" r="12" fill="white" />
                    <path
                      d="M20 18L24 22L20 26"
                      stroke="black"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <filter
                      id="filter0_ddd_888_6173"
                      x="0"
                      y="0"
                      width="44"
                      height="44"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect1_dropShadow_888_6173" />
                      <feOffset dy="2" />
                      <feGaussianBlur stdDeviation="2" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0" />
                      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_888_6173" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset dy="4" />
                      <feGaussianBlur stdDeviation="2.5" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0" />
                      <feBlend mode="normal" in2="effect1_dropShadow_888_6173" result="effect2_dropShadow_888_6173" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feOffset />
                      <feGaussianBlur stdDeviation="5" />
                      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0" />
                      <feBlend mode="normal" in2="effect2_dropShadow_888_6173" result="effect3_dropShadow_888_6173" />
                      <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow_888_6173" result="shape" />
                    </filter>
                  </defs>
                </svg>
              )}
            </div>
          )}
        </div>

        {/* 展示溯源-预览 md格式直接预览 */}
        {previewOpen && (
          <PreviewPage
            open={previewOpen}
            fileInfo={{
              file_name: isMarkdownFile(info?.title) ? info?.title : 'xxxx.chunk_pdf',
              file_path: info?.url,
              object_name: info?.source,
              id: info?.detail?.[0]?.chunk_id
            }}
            setOpen={setPreviewOpen}
            knowledgeTraceInfo={info} // 传递知识库溯源信息给PreviewPage
          />
        )}
      </div>
    </>
  )
}

export default PopoverCont
